import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { AudioLines, Loader2, Mic2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "./ConfirmDialog";
import VoiceStylePicker from "./voiceover/VoiceStylePicker";
import VoiceoverCard from "./voiceover/VoiceoverCard";
import SceneVoiceoverRow from "./voiceover/SceneVoiceoverRow";

export default function VoiceoverPanel({
  projectId, project, scenes = [], assets = [], hasScript, canEdit, onChange,
}) {
  const confirm = useConfirm();
  const [meta, setMeta] = useState({ mock: false, configured: false, provider: "elevenlabs", model: "eleven_multilingual_v2", default_voice_style: "narrator" });
  const [voiceStyle, setVoiceStyle] = useState(null);
  const [generatingFull, setGeneratingFull] = useState(false);
  const [generatingScene, setGeneratingScene] = useState(null);
  const [working, setWorking] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [jobError, setJobError] = useState(null);
  const [fittingDuration, setFittingDuration] = useState(false);
  const notifiedJobRef = useRef(null);
  const voiceToastRef = useRef(null);
  const jobKindRef = useRef("full");
  const activeStatus = String(activeJob?.status || "").toLowerCase();
  const jobActive = activeStatus === "queued" || activeStatus === "running";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tts/meta");
        setMeta(data);
        if (!voiceStyle) {
          const projectVoice = String(project?.voice_style || project?.voice_id || "").toLowerCase();
          const styles = (data.styles || []).map((s) => s.id);
          const matched = styles.find(
            (style) => projectVoice === style || projectVoice.includes(style),
          );
          setVoiceStyle(
            matched || data.default_voice_style || "narrator",
          );
        }
      } catch {/* noop */}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const voiceovers = useMemo(
    () => (assets || []).filter((a) => a.asset_type === "voiceover_audio"),
    [assets],
  );
  const fullScriptVOs = voiceovers.filter((v) => !v.scene_id);
  const sceneVOs = voiceovers.filter((v) => v.scene_id);
  const selectedFullVO = fullScriptVOs.find(
    (v) => v.id === project?.selected_voiceover_asset_id,
  );
  const selectedTimingIssue = selectedFullVO && (
    selectedFullVO.duration_fit_required
    || selectedFullVO.duration_status === "unmeasured"
    || selectedFullVO.duration_status === "outside_safe_range"
  );
  const selectedFit = selectedFullVO?.autofit_suggestion
    || project?.timing?.autofit_suggestion
    || null;
  const sceneVOMap = useMemo(() => {
    const m = {};
    sceneVOs.forEach((v) => {
      const list = m[v.scene_id] || (m[v.scene_id] = []);
      list.push(v);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) =>
      String(b.created_at || "").localeCompare(String(a.created_at || ""))
    ));
    return m;
  }, [sceneVOs]);
  // Count distinct scenes, not audio files: two takes for one scene must not
  // produce values like "8/4 scenes".
  const sceneVOAudioCount = useMemo(
    () => new Set(sceneVOs.filter((v) => v.status !== "rejected").map((v) => v.scene_id)).size,
    [sceneVOs],
  );

  const generateFull = async () => {
    if (!hasScript) {
      toast.error("Generate the script first");
      return;
    }
    setGeneratingFull(true);
    setJobError(null);
    const tid = toast.loading("Generating full-script voiceover…");
    voiceToastRef.current = tid;
    try {
      const { data } = await api.post(
        `/projects/${projectId}/voiceover/generate-script`,
        { voice_style: voiceStyle, force: fullScriptVOs.length > 0 },
      );
      jobKindRef.current = "full";
      if (data?.view) {
        // Idempotent replay: the artifact already exists, no provider call ran.
        onChange(data.view);
        setGeneratingFull(false);
        toast.success(
          data.idempotency_result === "recovered"
            ? "Existing voiceover reused — no new charge"
            : "Voiceover ready",
          { id: tid },
        );
        return;
      }
      setActiveJob(data);
      toast.loading("Voiceover queued — generating in background…", { id: tid });
    } catch (err) {
      setGeneratingFull(false);
      const detail = err.response?.data?.detail;
      setJobError({
        code: detail?.code || null,
        message: formatApiError(detail) || err.message,
        detail: detail || null,
        jobId: detail?.job?.id || null,
        needsReconciliation: Boolean(detail?.needs_reconciliation),
        recoverable: Boolean(detail?.recoverable_audio),
      });
      toast.error("Voiceover failed", {
        id: tid,
        description: formatApiError(detail) || err.message,
      });
    }
  };

  // Poll the durable job.  A proxy/browser disconnect does not cancel the
  // provider call; the worker keeps writing and the job is recoverable.
  useEffect(() => {
    if (!activeJob?.id || !jobActive) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/projects/${projectId}/voiceover/jobs/${activeJob.id}`,
        );
        if (cancelled) return;
        setActiveJob(data);
        const status = String(data.status || "").toLowerCase();
        if (status === "succeeded" && notifiedJobRef.current !== data.id) {
          notifiedJobRef.current = data.id;
          setGeneratingFull(false);
          setGeneratingScene(null);
          setJobError(null);
          if (data.view) onChange(data.view);
          toast.success(
            data.completed_after_disconnect
              ? "Voiceover finished after the connection dropped"
              : "Voiceover ready",
            { id: voiceToastRef.current || undefined },
          );
        } else if (status === "failed" && notifiedJobRef.current !== data.id) {
          notifiedJobRef.current = data.id;
          setGeneratingFull(false);
          setGeneratingScene(null);
          const detail = data.error_detail || null;
          const message = data.error_message || "Voiceover generation failed";
          setJobError({
            code: data.error_code,
            message,
            detail,
            jobId: data.id,
            needsReconciliation: Boolean(data.needs_reconciliation || detail?.needs_reconciliation),
            recoverable: Boolean(detail?.autofit_suggestion || detail?.partial_path),
          });
          toast.error("Voiceover failed", {
            id: voiceToastRef.current || undefined,
            description: message,
          });
        }
      } catch (err) {
        // Transient poll failure (often a proxy blip) — keep polling.
        console.warn("[Voiceover] poll failed", err?.message);
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeJob?.id, jobActive, projectId, onChange]);

  // Keep the button state in sync if the job record is queued/running.
  useEffect(() => {
    if (!jobActive) return;
    if (jobKindRef.current === "scene") {
      setGeneratingScene(activeJob?.scene_id || null);
    } else {
      setGeneratingFull(true);
    }
  }, [jobActive, activeJob?.scene_id]);

  const generateScene = async (scene) => {
    if (!scene.narration_text) {
      toast.error("Scene has no narration text");
      return;
    }
    setGeneratingScene(scene.id);
    const tid = toast.loading(`Generating scene ${scene.scene_number} voiceover…`);
    voiceToastRef.current = tid;
    try {
      const { data } = await api.post(
        `/projects/${projectId}/scenes/${scene.id}/voiceover/generate`,
        { voice_style: voiceStyle },
      );
      jobKindRef.current = "scene";
      if (data?.view) {
        // Idempotent replay: this scene take already exists, no provider call ran.
        onChange(data.view);
        setGeneratingScene(null);
        toast.success(
          data.idempotency_result === "recovered"
            ? "Existing scene voiceover reused — no new charge"
            : "Scene voiceover ready",
          { id: tid },
        );
        return;
      }
      setActiveJob(data);
      toast.loading("Scene voiceover queued — generating in background…", { id: tid });
    } catch (err) {
      toast.error("Scene voiceover failed", {
        id: tid,
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
      setGeneratingScene(null);
    }
  };

  const reconcileJob = async () => {
    if (!jobError?.jobId) return;
    setWorking("reconcile");
    const tid = toast.loading("Reconciling the saved recording…");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/voiceover/jobs/${jobError.jobId}/reconcile`,
      );
      onChange(data.view);
      setJobError(null);
      setActiveJob(null);
      toast.success("Saved recording recovered without another provider call", { id: tid });
    } catch (err) {
      toast.error("Reconcile failed", {
        id: tid,
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setWorking(null);
    }
  };

  const fitDuration = async (to) => {
    setFittingDuration(true);
    const tid = toast.loading(`Fitting project to ${to}s…`);
    try {
      const { data } = await api.post(`/projects/${projectId}/render/autofit-duration`);
      const full = await api.get(`/projects/${projectId}`);
      onChange(full.data);
      setJobError(null);
      toast.success(`Project duration fitted to ${data.autofit.to}s`, { id: tid });
    } catch (err) {
      toast.error("Duration fit failed", {
        id: tid,
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setFittingDuration(false);
    }
  };

  const restore = async (asset) => {
    setWorking(asset.id);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/voiceover/${asset.id}/restore`,
      );
      onChange(data);
      toast.success("Recording restored and selected — no new provider call");
    } catch (err) {
      toast.error("Restore failed", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setWorking(null);
    }
  };

  const select = async (asset) => {
    setWorking(asset.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/voiceover/${asset.id}/select`);
      onChange(data);
      toast.success("Selected");
    } catch (err) {
      toast.error("Select failed", { description: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setWorking(null);
    }
  };

  const reject = async (asset) => {
    setWorking(asset.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/voiceover/${asset.id}/reject`);
      onChange(data);
      toast.success("Rejected");
    } catch (err) {
      toast.error("Reject failed", { description: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setWorking(null);
    }
  };

  const remove = async (asset) => {
    const ok = await confirm({
      title: "Delete this voiceover?",
      description: "The audio file will be permanently removed.",
      confirmLabel: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    setWorking(asset.id);
    try {
      const { data } = await api.delete(`/projects/${projectId}/voiceover/${asset.id}`);
      onChange(data);
      toast.success("Deleted");
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
            {meta.provider} · {meta.model}
          </div>
          <h2 className="text-lg font-semibold text-white mt-1 flex items-center gap-2">
            <Mic2 size={16} className="text-[#7B61FF]" /> Voiceover
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {jobActive && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#00E5FF] border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-2 py-0.5 rounded-sm">
              {activeStatus === "queued" ? "Queued" : "Generating"} · survives disconnects
            </span>
          )}
          {meta.mock ? (
            <span
              data-testid="vo-mock-badge"
              className="font-mono text-[10px] uppercase tracking-widest text-[#FFB020] border border-[#FFB020]/30 bg-[#FFB020]/10 px-2 py-0.5 rounded-sm"
            >
              Mock TTS
            </span>
          ) : (
            <span
              data-testid="vo-live-badge"
              className="font-mono text-[10px] uppercase tracking-widest text-[#00FF66] border border-[#00FF66]/30 bg-[#00FF66]/10 px-2 py-0.5 rounded-sm"
            >
              {meta.configured === false
                ? `${meta.provider || "Voice provider"} · Setup required`
                : `${meta.provider || "Voice provider"} · Live`}
            </span>
          )}
        </div>
      </div>

      {jobError && (
        <div
          data-testid="vo-job-error"
          className="border border-[#FF3366]/40 bg-[#FF3366]/5 rounded-sm px-4 py-3 flex flex-wrap items-center gap-3"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3366]">
              {jobError.needsReconciliation ? "Voiceover needs reconciliation" : "Voiceover failed"}
              {jobError.code ? ` · ${jobError.code}` : ""}
            </div>
            <p className="text-sm text-zinc-300 mt-1">{jobError.message}</p>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">
              {jobError.needsReconciliation
                ? "A provider call may have completed. Reconcile the saved attempt before any new chargeable request."
                : "The job record is durable. A recording that was produced is never discarded by a timing mismatch."}
            </p>
          </div>
          {canEdit && jobError.jobId && jobError.needsReconciliation && (
            <button
              onClick={reconcileJob}
              disabled={working === "reconcile"}
              className="font-mono text-[11px] uppercase tracking-widest border border-[#FFB020]/60 text-[#FFB020] hover:bg-[#FFB020]/10 px-3 py-2.5 rounded-sm disabled:opacity-50"
            >
              {working === "reconcile" ? "Reconciling…" : "Reconcile saved recording"}
            </button>
          )}
        </div>
      )}

      {selectedTimingIssue && (
        <div
          data-testid="vo-duration-warning"
          className="border border-[#FFB020]/40 bg-[#FFB020]/5 rounded-sm px-4 py-3 flex flex-wrap items-center gap-3"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB020]">
              Recording kept · timing {selectedFullVO.duration_status || "outside target"}
            </div>
            <p className="text-sm text-zinc-300 mt-1">
              {selectedFullVO.duration_message
                || (selectedFullVO.duration
                  ? `This take is ${Number(selectedFullVO.duration).toFixed(1)}s against a ${Number(project?.target_duration || 0)}s project.`
                  : "Audio is saved but its duration could not be measured, so render preflight cannot judge it.")}
            </p>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">
              The selected duration is unchanged. The audio is downloadable and is never truncated.
            </p>
          </div>
          {canEdit && selectedFit?.to && selectedFit.to !== selectedFit.from && (
            <button
              data-testid="vo-fit-duration-btn"
              onClick={() => fitDuration(selectedFit.to)}
              disabled={fittingDuration}
              className="font-mono text-[11px] uppercase tracking-widest bg-[#FFB020] text-black px-3 py-2.5 rounded-sm hover:bg-[#FFC64D] disabled:opacity-50"
            >
              {fittingDuration ? "Fitting…" : `Use explicit ${selectedFit.from}s → ${selectedFit.to}s fit`}
            </button>
          )}
        </div>
      )}

      {canEdit && <VoiceStylePicker value={voiceStyle} onChange={setVoiceStyle} />}

      {/* Full-script section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Full script voiceover
            </div>
            <div className="text-sm text-zinc-300 mt-1">
              One audio for the entire video. Used as the master narration.
            </div>
          </div>
          {canEdit && (
            <button
              data-testid="vo-generate-full-btn"
              onClick={generateFull}
              disabled={generatingFull || !hasScript}
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-[#00E5FF] text-black hover:bg-[#33EFFF] disabled:opacity-50 px-4 py-2.5 rounded-sm transition-colors"
            >
              {generatingFull ? <Loader2 size={12} className="animate-spin" /> : <AudioLines size={12} />}
              {fullScriptVOs.length > 0 ? "Regenerate full" : "Generate full script"}
            </button>
          )}
        </div>
        {!hasScript && (
          <div className="border border-zinc-800 border-dashed p-6 text-center rounded-sm text-sm text-zinc-500">
            Generate the script first.
          </div>
        )}
        {hasScript && fullScriptVOs.length === 0 && (
          <div className="border border-zinc-800 border-dashed p-6 text-center rounded-sm text-sm text-zinc-500">
            No full-script voiceover yet.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fullScriptVOs
            .slice()
            .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
            .map((v) => (
              <VoiceoverCard
                key={v.id}
                asset={v}
                label="Full script"
                isSelectedForProject={project?.selected_voiceover_asset_id === v.id}
                working={working}
                canEdit={canEdit}
                onSelect={select}
                onReject={reject}
                onDelete={remove}
                onRestore={restore}
              />
            ))}
        </div>
      </section>

      {/* Per-scene section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Scene voiceovers
            </div>
            <div className="text-sm text-zinc-300 mt-1">
              Per-scene narration — required for the future ffmpeg render queue.
            </div>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {sceneVOAudioCount}/{scenes.length} scenes have audio
            {sceneVOs.length > sceneVOAudioCount ? " · extra takes preserved" : ""}
          </div>
        </div>
        {scenes.length === 0 ? (
          <div className="border border-zinc-800 border-dashed p-6 text-center rounded-sm text-sm text-zinc-500">
            Generate scenes first.
          </div>
        ) : (
          <div className="space-y-3">
            {scenes
              .slice()
              .sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0))
              .map((scene) => (
                <SceneVoiceoverRow
                  key={scene.id}
                  scene={scene}
                  voiceovers={sceneVOMap[scene.id] || []}
                  generating={generatingScene === scene.id}
                  working={working}
                  canEdit={canEdit}
                  onGenerate={generateScene}
                  onSelect={select}
                  onReject={reject}
                  onDelete={remove}
                  onRestore={restore}
                />
              ))}
          </div>
        )}
      </section>
    </div>
  );
}
